<template>
    <div
        class="bg-white/70 backdrop-blur-sm duration-200 ease-out rounded-lg shadow overflow-hidden z-20"
        :class="{
            'w-64': !collapsed,
            'w-10': collapsed,
            'pointer-events-none': store.isDragging,
        }"
    >
        <!-- Header -->
        <div
            class="flex items-center justify-between p-2 border-b border-gray-200/50"
        >
            <h3
                v-show="!collapsed"
                class="text-sm font-semibold text-gray-800"
            >
                Pohledy
            </h3>
            <button
                class="flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-100/70 transition-colors"
                @click="collapsed = !collapsed"
                :title="collapsed ? 'Rozbalit pohledy' : 'Sbalit pohledy'"
            >
                <span
                    class="esri-icon transition-transform duration-200 text-lg!"
                    :class="{
                        'esri-icon-right-triangle-arrow': collapsed,
                        'esri-icon-left-triangle-arrow': !collapsed,
                    }"
                ></span>
            </button>
        </div>

        <!-- Viewpoints Grid -->
        <div
            v-show="!collapsed"
            class="p-3 space-y-1 max-h-80 overflow-y-auto"
        >
            <!-- Loading State -->
            <div
                v-if="loading"
                class="flex items-center justify-center py-4"
            >
                <span class="text-sm">Načítání pohledů...</span>
            </div>

            <!-- Viewpoints List -->
            <button
                v-else
                v-for="viewpoint in viewpoints"
                :key="viewpoint.id"
                class="w-full flex items-center gap-3 p-2 rounded-md hover:bg-gray-100/70 transition-colors group"
                @click="goToViewpoint(viewpoint)"
                title="Přesunout na pohled"
            >
                <div class="shrink-0">
                    <img
                        v-if="
                            viewpoint.thumbnailPath && !imageError[viewpoint.id]
                        "
                        :src="viewpoint.thumbnailPath"
                        :alt="viewpoint.title"
                        class="w-18 h-auto aspect-4/3 object-cover rounded border border-gray-200/50"
                        @error="onImageError(viewpoint)"
                    />
                    <div
                        v-if="imageError[viewpoint.id]"
                        class="w-18 h-auto aspect-4/3 object-cover rounded border border-gray-200/50"
                    />
                </div>
                <span
                    class="text-sm text-gray-800 text-left font-medium group-hover:text-gray-900"
                >
                    {{ viewpoint.title }}
                </span>
            </button>

            <!-- Empty State -->
            <div
                v-if="!loading && viewpoints.length === 0"
                class="flex items-center justify-center py-4"
            >
                <span class="text-sm text-gray-500"
                    >Žádné dostupné pohledy</span
                >
            </div>
        </div>

        <!-- Collapsed State Icon -->
        <div
            v-show="collapsed"
            class="p-2 flex justify-center"
            title="Rozbalit pohledy"
            @click="collapsed = !collapsed"
        >
            <span class="esri-icon esri-icon-media text-lg!"></span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue"
import Camera from "@arcgis/core/Camera"
import { useStore } from "../store"
import type { Viewpoint } from "../types/viewpoints"

const store = useStore()

const collapsed = ref(window.innerWidth < 640) // Start collapsed on small screens
const viewpoints = ref<Viewpoint[]>([])
const loading = ref(true)
const imageError = ref<Record<string, boolean>>({})

// Load viewpoints from JSON file
async function loadViewpoints() {
    try {
        const response = await fetch("./viewpoints.json")
        if (!response.ok) {
            throw new Error("Failed to load viewpoints")
        }
        const data = await response.json()
        viewpoints.value = data
    } catch (error) {
        console.error("Error loading viewpoints:", error)
        // Fallback to empty array if loading fails
        viewpoints.value = []
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    loadViewpoints()
})

watch(collapsed, newVal => {
    if (!newVal) {
        if (window.innerWidth < 640) {
            store.activeViewpoint = null
        }
    }
})

function goToViewpoint(viewpoint: Viewpoint) {
    store.activeViewpoint = viewpoint
    if (window.innerWidth < 640) {
        collapsed.value = true
    }
    const view = window.app.view
    const viewRight = window.app.viewRight
    const camera = new Camera({
        position: {
            longitude: viewpoint.camera.longitude,
            latitude: viewpoint.camera.latitude,
            z: viewpoint.camera.z,
        },
        tilt: viewpoint.camera.tilt,
        heading: viewpoint.camera.heading,
    })
    if (view) {
        view.goTo(camera, {
            duration: 1500,
            easing: "ease-in-out",
        })
    }
    if (viewRight) {
        viewRight.goTo(camera, {
            duration: 1500,
            easing: "ease-in-out",
        })
    }
}

function onImageError(viewpoint: Viewpoint) {
    imageError.value[viewpoint.id] = true
    // const img = event.target as HTMLImageElement
    // Use a placeholder image in case the thumbnail doesn't exist
    // img.src = "./img/viewpoints/placeholder.jpg"
}
</script>
